import {
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  DataStateChangeEvent,
  FilterableSettings,
  FilterService,
  GridComponent,
  GroupableSettings,
  SelectionEvent,
} from '@progress/kendo-angular-grid';
import { PagerSettings } from '@progress/kendo-angular-grid';
import {
  CompositeFilterDescriptor,
  GroupDescriptor,
  State,
} from '@progress/kendo-data-query';
import {
  ColumnGroup,
  ColumnOptions,
  GridRowType,
  GridState,
} from '../../models';
import {
  ColumnTemplateDirective,
  CommandColumnTemplateDirective,
  FilterTemplateDirective,
  GroupColumnHeaderTemplateDirective,
  HeaderTemplateDirective,
} from '../../directives';
import { TokenService } from '@auth';

type GridMode = 'view' | 'edit';

@Component({
  selector: 'app-data-grid',
  templateUrl: './data-grid.component.html',
  styleUrl: './data-grid.component.scss',
})
export class DataGridComponent implements OnChanges, OnInit {
  @Output()
  dataStateChange: EventEmitter<DataStateChangeEvent> =
    new EventEmitter<DataStateChangeEvent>();

  @Output()
  filterChange: EventEmitter<CompositeFilterDescriptor> =
    new EventEmitter<CompositeFilterDescriptor>();

  @Output()
  groupChange: EventEmitter<GroupDescriptor[]> = new EventEmitter<
    GroupDescriptor[]
  >();

  @Output()
  valueChange: EventEmitter<void> = new EventEmitter<void>();

  @ViewChildren('warnings', { read: ElementRef })
  warnings?: QueryList<ElementRef>;

  @Output()
  rowSelected: EventEmitter<SelectionEvent> =
    new EventEmitter<SelectionEvent>();

  @Input()
  state: State = {};

  @Input()
  maxHeight?: number;

  @Input()
  maxWidth?: number;

  @Input()
  minHeight?: number;

  @Input()
  minWidth?: number;

  @Input()
  paginate: boolean = true;

  @Input()
  isQuickView: boolean = false;

  @Input()
  scrollable: 'none' | 'scrollable' | 'virtual' = 'scrollable';

  @Input()
  groupable: GroupableSettings = {
    enabled: false,
    showFooter: false,
  };

  isLoading: boolean = false;

  @Input()
  group: GroupDescriptor[] = [];

  @Input()
  dataItems: any = { data: [], total: 0 };

  @Input()
  columns: ColumnOptions[] = [];

  @Input()
  columnGroups?: ColumnGroup[];

  defaultFontSize = 0.875;

  @Input()
  fontSize: number = this.defaultFontSize;

  @ContentChildren(FilterTemplateDirective)
  filterTemplates!: QueryList<FilterTemplateDirective>;

  @ContentChildren(ColumnTemplateDirective)
  columnTemplates!: QueryList<ColumnTemplateDirective>;

  @ContentChildren(HeaderTemplateDirective)
  headerTemplates!: QueryList<HeaderTemplateDirective>;

  @ContentChildren(GroupColumnHeaderTemplateDirective)
  groupHeaderTemplates!: QueryList<GroupColumnHeaderTemplateDirective>;

  @ContentChild('toolbarTemplate')
  toolbarTemplate?: TemplateRef<any>;

  @ContentChild('noRecordsTemplate')
  noRecordsTemplate?: TemplateRef<any>;

  @ViewChild(GridComponent, { static: false })
  grid!: GridComponent;

  @ContentChildren(CommandColumnTemplateDirective)
  commandColumnTemplates: QueryList<CommandColumnTemplateDirective> =
    new QueryList<CommandColumnTemplateDirective>();

  @Input()
  hideCommands: boolean = false;

  @Input()
  filterable!: FilterableSettings;

  pagerSettings: PagerSettings = {
    buttonCount: 10,
    info: true,
    type: 'numeric',
    previousNext: true,
    pageSizes: GridState.getPagerOptionsByFontSize(this.fontSize),
  };

  @Input()
  mode: GridMode = 'view';

  headerStyle: { [key: string]: string } = {
    'font-size': this.fontSize + 'rem',
    'justify-content': 'center',
  };

  conceptKey?: string | null;

  constructor(private tokenService: TokenService) {}

  async ngOnInit() {
    this.conceptKey = await this.tokenService.currentConceptKey() ?? null;

    this.columns.forEach(col => {
      col.hidden = col.hideForConcept?.includes(this.conceptKey?? '') ? true : col.hidden;
    })
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['fontSize'] && this.fontSize && this.grid) {
      this.grid.columns.forEach((column) => {
        column.width =
          column.width != -1
            ? (column.width * this.fontSize) / this.defaultFontSize
            : -1;
      });

      this.grid.pageable = {
        buttonCount: 10,
        info: true,
        type: 'numeric',
        previousNext: true,
        pageSizes: GridState.getPagerOptionsByFontSize(this.fontSize),
        responsive: true,
      };
    }

    if (changes['columns'] && this.columns) {
      if (!changes['columns'].firstChange) {
        this.setTemplates();
      }
      this.columns = this.columns.filter((col) => col.group === undefined);
    }

    this.headerStyle['font-size'] = this.fontSize + 'rem';
  }

  @Input()
  selectable: boolean = false;

  @Input() selectedRow!: number;

  ngAfterViewInit(): void {
    this.setTemplates();
  }

  onSelectRow(event: SelectionEvent): void {
    this.rowSelected.emit(event);
  }

  setTemplates() {
    this.columns.forEach((column) => {
      const filterTemplate = this.filterTemplates.find(
        (template) => template.field === column.field,
      );
      column.filterTemplate = filterTemplate?.template;

      const columnTemplate = this.columnTemplates.find(
        (template) => template.field === column.field,
      );
      column.columnTemplate = columnTemplate?.template;

      const groupHeaderTemplate = this.groupHeaderTemplates.find(
        (template) => template.field === column.field,
      );
      column.groupHeaderTemplate = groupHeaderTemplate?.template;

      const headerTemplate = this.headerTemplates.find(
        (template) => template.field === column.field,
      );
      column.headerTemplate = headerTemplate?.template;
    });

    this.columnGroups?.forEach((group) => {
      group.columns.forEach((column) => {
        const filterTemplate = this.filterTemplates.find(
        (template) => template.field === column.field,
      );
      column.filterTemplate = filterTemplate?.template;

      const columnTemplate = this.columnTemplates.find(
        (template) => template.field === column.field,
      );
      column.columnTemplate = columnTemplate?.template;

      const groupHeaderTemplate = this.groupHeaderTemplates.find(
        (template) => template.field === column.field,
      );
      column.groupHeaderTemplate = groupHeaderTemplate?.template;

      const headerTemplate = this.headerTemplates.find(
        (template) => template.field === column.field,
      );
      column.headerTemplate = headerTemplate?.template;
      });
    });
  }

  onFilterChange(event: CompositeFilterDescriptor): void {
    this.filterChange.emit(event);
  }

  onDataStateChange(event: DataStateChangeEvent): void {
    this.dataStateChange.emit(event);
  }

  onValueChange(
    dataItem: { [key: string]: any } & GridRowType,
    columnField: string,
    emitEvent: boolean = true,
  ): void {
    const oldValue = dataItem.originalValues.get(columnField);
    const newValue = dataItem[columnField];
    if (oldValue == newValue) {
      dataItem.dirty.set(columnField, false);
    } else {
      dataItem.dirty.set(columnField, true);
    }

    if (emitEvent) this.valueChange.emit();
  }

  onGroupChange(event: GroupDescriptor[]): void {
    this.groupChange.emit(event);
  }

  onCustomFilterChange(
    column: ColumnOptions,
    event: any,
    filterService: FilterService,
  ) {
    const selected = event as { name: string; value: any } | null;

    if (selected?.value === undefined) return;

    filterService.filter({
      filters: [
        {
          field: column.field,
          operator: 'eq',
          value: selected?.value,
        },
      ],
      logic: 'or',
    });
  }

  rowCallback = (context: { dataItem: any; index: number }) => {
    return {
      'highlight-row':
        this.selectedRow !== undefined &&
        context.dataItem.id === this.selectedRow,
    };
  };

  scrollToRow(rowId: number): void {
    document.querySelectorAll('.k-grid tr.k-selected').forEach((row) => {
      row.classList.remove('k-selected');
    });

    setTimeout(() => {
      const rowIndex = this.dataItems.data.findIndex(
        (item: { id: number }) => item.id === rowId,
      );
      if (rowIndex >= 0) {
        const row =
          this.grid?.wrapper?.nativeElement.querySelectorAll('tbody tr')[
            rowIndex
          ];
        if (row) {
          row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    });
  }
}
